/**
 * Verified connectivity signal — the single source of truth for
 * "are we online?" across the field app.
 *
 * WHY THIS EXISTS
 * The whole field workflow used to read the browser's `navigator.onLine`
 * flag directly and gate every save on it: `if (!isOnline) queue()`. But
 * `navigator.onLine === false` is a notorious browser FALSE-NEGATIVE — it
 * flips to "offline" with full cellular signal on captive-portal probes,
 * tower hand-offs, VPN reconnects, and OS sleep/wake. When it lied, the app
 * diverted every write into the IndexedDB outbox instead of hitting the
 * server, the tech (or the office) redid the work, and then the queued copy
 * replayed on reconnect — the "constantly offline / double work" report.
 *
 * THE RULE
 * `navigator.onLine === true` is trusted as-is: when the browser says it can
 * reach the network, it can. `navigator.onLine === false` is treated as a
 * SUSPICION, not a fact — we confirm with a lightweight reachability probe
 * against `/health` before ever reporting offline, and keep re-probing while
 * suspect so the state recovers on its own even if the `online` event never
 * fires (a known iOS home-screen-PWA gap). We also re-probe the moment the app
 * returns to the foreground: mobile browsers freeze background tabs and suspend
 * the recovery poll, so a tech who walked out of a dead zone and reopened the
 * app must recover instantly, not wait out a poll that wasn't even running.
 * Only a probe that actually fails flips us to offline. The offline outbox
 * stays exactly as it was — a real dead-zone still queues and drains — it just
 * stops firing on phantom drops.
 *
 * This module deliberately imports no React (so `save-outbox.ts` and other
 * non-hook callers can read it) and holds a single process-wide state.
 */

/** Endpoint used to confirm reachability. DB-free and fast server-side
 *  (`src/server/app.ts` `/health`), same-origin, already proxied in dev. */
const PROBE_URL = "/health";
/** A probe that can't answer in this window counts as one failed attempt.
 *  Generous enough for a slow cellular round-trip, bounded enough to correct a
 *  false-negative quickly. */
const PROBE_TIMEOUT_MS = 4_000;
/** Weak-signal hardening: a real 2-bar link drops the odd packet, and one lost
 *  packet must NOT declare the tech offline. We only believe we're offline
 *  after this many probes in a row fail (each with its own timeout). */
const PROBE_ATTEMPTS = 2;
/** While confirmed offline, re-probe on this cadence so we auto-recover
 *  without needing the (sometimes-missing) `online` event. */
const RECHECK_INTERVAL_MS = 15_000;

function readNavigatorOnline(): boolean {
    return typeof navigator === "undefined" ? true : navigator.onLine;
}

/** Optimistic start: honor the browser's initial claim. If it boots claiming
 *  offline we verify immediately (below) rather than trusting a possibly
 *  stale flag from a cold PWA reopen. */
let verifiedOnline = readNavigatorOnline();
let probeInFlight = false;
let recheckTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
    for (const cb of listeners) cb();
}

function setVerified(next: boolean): void {
    if (verifiedOnline === next) return;
    verifiedOnline = next;
    notify();
}

function clearRecheck(): void {
    if (recheckTimer !== null) {
        clearTimeout(recheckTimer);
        recheckTimer = null;
    }
}

function scheduleRecheck(): void {
    if (recheckTimer !== null) return;
    recheckTimer = setTimeout(() => {
        recheckTimer = null;
        void probeConnectivityNow();
    }, RECHECK_INTERVAL_MS);
}

/** True if `/health` answered at all. ANY HTTP response — even a 5xx — proves
 *  the server is REACHABLE, which is all the online/offline gate cares about;
 *  a server error is not a connectivity problem and must not divert saves.
 *  Retried up to PROBE_ATTEMPTS so a single dropped packet on a weak link
 *  doesn't flip a working (if flaky) connection to "offline". */
async function reachable(): Promise<boolean> {
    for (let attempt = 0; attempt < PROBE_ATTEMPTS; attempt++) {
        try {
            await fetch(PROBE_URL, {
                method: "GET",
                cache: "no-store",
                signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
            });
            return true;
        } catch {
            // Network error / DNS / timeout / abort on this attempt — retry
            // once more before we believe the link is genuinely down.
        }
    }
    return false;
}

/**
 * Run one reachability probe and update the verified state from the result.
 * Callers use it to actively re-confirm connectivity (e.g. `save-outbox`
 * after a failed write). Returns the resolved online state.
 */
export async function probeConnectivityNow(): Promise<boolean> {
    // The browser flipped back to online while we were suspect — trust it and
    // stop probing.
    if (readNavigatorOnline()) {
        clearRecheck();
        setVerified(true);
        return true;
    }
    if (probeInFlight) return verifiedOnline;
    probeInFlight = true;
    let ok: boolean;
    try {
        ok = await reachable();
    } finally {
        probeInFlight = false;
    }
    // Guard the race: the `online` event may have fired during the probe.
    if (readNavigatorOnline()) {
        clearRecheck();
        setVerified(true);
        return true;
    }
    setVerified(ok);
    if (ok) clearRecheck();
    else scheduleRecheck();
    return ok;
}

function handleOnline(): void {
    clearRecheck();
    setVerified(true);
}

function handleOffline(): void {
    // Do NOT trust the flag. Keep the last known (optimistic) state until a
    // probe actually fails — a false-negative is corrected without ever
    // showing the tech an offline state or queuing a single save.
    void probeConnectivityNow();
}

/**
 * The app returned to the foreground (tab re-focused / PWA reopened). Mobile
 * browsers FREEZE background tabs — iOS home-screen PWAs and mobile Chrome
 * both suspend timers on a backgrounded tab — so while we were away the
 * `RECHECK_INTERVAL_MS` recovery poll may not have run at all, and the `online`
 * event is the same signal we already can't trust on iOS PWA. If we're
 * currently confirmed offline, re-confirm right now so a tech who walked out of
 * a dead zone and reopened the app recovers instantly instead of waiting out a
 * (possibly-frozen) poll. When we already believe we're online this is a no-op:
 * `probeConnectivityNow()` trusts `navigator.onLine === true` without a network
 * hit, so an ordinary foreground adds no `/health` traffic.
 */
function handleForeground(): void {
    if (verifiedOnline) return;
    void probeConnectivityNow();
}

if (typeof window !== "undefined") {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    // Foreground-recovery: the one event that always fires when the tech comes
    // back to the app, even when the frozen-tab poll and the `online` event
    // don't. `focus` covers desktop tab switches; `visibilitychange` covers a
    // reopened mobile PWA (where `focus` alone is unreliable).
    window.addEventListener("focus", handleForeground);
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") handleForeground();
        });
    }
    // Booted while the browser claims offline? Verify instead of believing it.
    if (!readNavigatorOnline()) void probeConnectivityNow();
}

/** The verified online state. Use this everywhere `navigator.onLine` was read
 *  to decide whether to hit the network or queue. */
export function getVerifiedOnline(): boolean {
    return verifiedOnline;
}

/** Subscribe to verified-connectivity changes. Shaped for `useSyncExternalStore`. */
export function subscribeConnectivity(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

/** Test-only: reset module state between cases (jsdom keeps the singleton
 *  alive across tests). Not part of the public runtime surface. */
export function __resetConnectivityForTests(): void {
    clearRecheck();
    probeInFlight = false;
    listeners.clear();
    verifiedOnline = readNavigatorOnline();
}
